import { createGlobalStyle } from "styled-components";
import theme from "styled-theming";
import colors from "./colors";
import "./fonts.css"
import { device, hexToRgb } from "./helpers";
import { DetailedHTMLProps, HTMLAttributes } from "react";

export const NavBarHeight = "80px"
export const PageHeaderContainerHeightWithMargin = "104px";
export const IndentedMarginTopValue = 30;
export const IndentedMarginTop = `${IndentedMarginTopValue}px`;
export const IndentedMarginTopSmall = "1rem";
export const UserThemeLocalStorageKey = "userTheme";

export enum ThemeType {
    light = "light",
    dark = "dark"
}

export interface IVariantProps<TVariantType> extends DetailedHTMLProps<HTMLAttributes<HTMLElement>, HTMLElement> {
    variant?: TVariantType;
}

const textColor = theme("mode", {
    [ThemeType.light]: colors.black,
    [ThemeType.dark]: colors.grey_4
});

const backgroundColor = theme("mode", {
    [ThemeType.light]: colors.white,
    [ThemeType.dark]: colors.dark
});

const footerBackgroundColor = theme("mode", {
    [ThemeType.light]: colors.grey_5,
    [ThemeType.dark]: colors.dark
})

const footerBorderColor = theme("mode", {
    [ThemeType.light]: colors.purplePrimary,
    [ThemeType.dark]: colors.white
})

const footerColor = theme("mode", {
    [ThemeType.light]: colors.black,
    [ThemeType.dark]: colors.white
})

export const bpmnPaletteBackgroundColor = theme("mode", {
    [ThemeType.light]: colors.grey_5,
    [ThemeType.dark]: colors.dark2
})

const bpmnPaletteBackgroundColorHover = theme("mode", {
    [ThemeType.light]: colors.dark5,
    [ThemeType.dark]: colors.dark
})

const bpmnPopupShadowColor = theme("mode", {
    [ThemeType.light]: colors.dark5,
    [ThemeType.dark]: colors.dark3
})

export const bpmnPaletteColor = theme("mode", {
    [ThemeType.light]: colors.black,
    [ThemeType.dark]: colors.grey_4
})

export const bpmnPaletteColorUrlEncoded = theme("mode", {
    [ThemeType.light]: encodeURIComponent(colors.black),
    [ThemeType.dark]: encodeURIComponent(colors.grey_4)
})

export const bpmnPaletteDisabledColorUrlEncoded = theme("mode", {
    [ThemeType.light]: encodeURIComponent(colors.darkPurpleTagBorder),
    [ThemeType.dark]: encodeURIComponent(colors.grey_1)
})

const bpmnElementAccentColor = theme("mode", {
    [ThemeType.light]: colors.orangePrimary,
    [ThemeType.dark]: colors.purpleTertiary
})

const bpmnElementAccentColorUrlEncoded = theme("mode", {
    [ThemeType.light]: encodeURIComponent(colors.orangePrimary),
    [ThemeType.dark]: encodeURIComponent(colors.purpleTertiary)
})

const bpmnSearchResultSelectedColor = theme("mode", {
    [ThemeType.light]: colors.orangePrimary,
    [ThemeType.dark]: colors.purpleSecondary
});

const bpmnSearchBackgroundColor = theme("mode", {
    [ThemeType.light]: colors.white,
    [ThemeType.dark]: colors.grey_1
});

const bpmnSearchBackgroundColorSelected = theme("mode", {
    [ThemeType.light]: colors.orangePrimary,
    [ThemeType.dark]: colors.purpleSecondary
})

const bpmnContextPadBackgroundColor = theme("mode", {
    [ThemeType.light]: colors.white,
    [ThemeType.dark]: colors.dark2
})

const bpmnContextPadEntryHoverBackgroundColor = theme("mode", {
    [ThemeType.light]: colors.white,
    [ThemeType.dark]: colors.dark
})

const bpmnLabelColor = theme("mode", {
    [ThemeType.light]: colors.black,
    [ThemeType.dark]: colors.grey_4
})

const bpmnNotRunLabelColor = theme("mode", {
    [ThemeType.light]: colors.grey_1,
    [ThemeType.dark]: colors.white
})

const bpmnElementColor = theme("mode", {
    [ThemeType.light]: colors.white,
    [ThemeType.dark]: colors.dark5
})

const bpmnDropAllowedColor = theme("mode", {
    [ThemeType.light]: colors.white,
    [ThemeType.dark]: colors.dark
})

export const datePickerBackgroundColor = theme("mode", {
    [ThemeType.light]: colors.white,
    [ThemeType.dark]: colors.dark2
})

const datePickerDayColor = theme("mode", {
    [ThemeType.light]: colors.black,
    [ThemeType.dark]: colors.grey_2
})

const datePickerDayHoverBackgroundColor = theme("mode", {
    [ThemeType.light]: colors.grey_3,
    [ThemeType.dark]: colors.dark
})

const datePickerDisabledDayColor = theme("mode", {
    [ThemeType.light]: colors.grey_2,
    [ThemeType.dark]: colors.dark
})

const datePickerHeaderBackgroundColor = theme("mode", {
    [ThemeType.light]: colors.grey_2,
    [ThemeType.dark]: colors.dark2
})

const datePickerHeaderColor = theme("mode", {
    [ThemeType.light]: colors.black,
    [ThemeType.dark]: colors.grey_2
})

export const linkColor = theme("mode", {
    [ThemeType.light]: colors.purplePrimary,
    [ThemeType.dark]: colors.purpleTertiary
})

const datePickerNavigationColor = theme("mode", {
    [ThemeType.light]: colors.grey_1,
    [ThemeType.dark]: colors.grey_1
})

const datePickerInputContainerBackgroundColor = theme("mode", {
    [ThemeType.light]: colors.white,
    [ThemeType.dark]: colors.dark2
})

const datePickerInputContainerColor = theme("mode", {
    [ThemeType.light]: colors.black,
    [ThemeType.dark]: colors.white
})

export const GlobalStyle = createGlobalStyle`
    :root {
        --toastify-toast-width: 420px;
        --toastify-color-dark: ${colors.dark2};
        --toastify-font-family: 'Gilroy-Regular', "regular-fallback";
        --base-font-size: 16px;
        --header-1-font-size: 2rem; // base 16px + 16px = 32px
        --header-2-font-size: 1.5rem; // base 16px + 8px = 24px
        --header-3-font-size: 1.25rem; // base 16px + 4px = 20px
        --header-4-font-size: 1.125rem; // base 16px + 2px = 18px
        --small-text-font-size: 0.875rem; // base 16px - 2px = 14px
    }
    html, body {
        font-family: 'Gilroy-Regular', "regular-fallback";
        margin: 0;
        padding: 0;
        font-size: var(--base-font-size);
        color: ${textColor};
        background-color: ${backgroundColor};
        scrollbar-gutter: stable;
    }
    html {
        box-sizing: border-box;

    }
    body {
        overflow: overlay;
        font-family: "Gilroy-Regular", "regular-fallback";

    }
    *, *:before, *:after {
        box-sizing: inherit;
    }
    h1,h2,h3,h4 {
        font-family: "Gilroy-Medium", "medium-fallback";
    }
    h1 {
        font-size: var(--header-1-font-size);
        font-weight: 500;
        margin: 0;
    }
    h2 {
        font-size: var(--header-2-font-size);
        font-weight: 500;
        margin: 0;
    }
    h3 {
        font-size: var(--header-3-font-size);
        font-weight: 500;
        margin: 0;
    }
    h4 {
        font-size: var(--header-4-font-size);
        font-weight: 500;
        margin: 0;
    }
    hr {
        border: 0.4px solid ${colors.grey_2};
        margin: 25px 0;
    }
    a {
        color: ${linkColor};
    }
    .pointer {
        cursor: pointer;
    }
    .full-width {
        width: 100%;
    }
    ::-webkit-scrollbar {
        width: 6px;
        height: 6px;
    }
    /* Track */
    ::-webkit-scrollbar-track {
        background: transparent;
    }
    
    /* Handle */
    ::-webkit-scrollbar-thumb {
        background: ${colors.grey_2};
        border-radius: 30px;
    }

    /* Handle on hover */
    ::-webkit-scrollbar-thumb:hover {
        background: ${colors.grey_1}; 
    }

    #footer {
        border-top: 1px solid;
        background-color: ${footerBackgroundColor};
        border-color: ${footerBorderColor};
        color: ${footerColor};
      
    }
    a {
        text-decoration: none;
    }
    .djs-parent {
        --palette-entry-color: ${bpmnPaletteColor};
        --palette-entry-hover-color: ${bpmnElementAccentColor};
        --palette-background-color: ${bpmnPaletteBackgroundColor};
        --palette-entry-selected-color: ${bpmnElementAccentColor};
        --context-pad-entry-hover-background-color: ${bpmnContextPadEntryHoverBackgroundColor};
        
        --shape-drop-allowed-fill-color: ${bpmnDropAllowedColor};
        --shape-connect-allowed-fill-color: ${bpmnDropAllowedColor};
        --shape-resize-preview-stroke-color: ${bpmnElementAccentColor};
        --shape-attach-allowed-stroke-color: ${bpmnElementAccentColor};
        
        --bendpoint-fill-color: ${bpmnElementAccentColor};
        --bendpoint-stroke-color: ${bpmnElementAccentColor};

        --element-dragger-color: ${bpmnElementAccentColor};
        --element-hover-outline-fill-color: ${bpmnElementAccentColor};
        --element-selected-outline-stroke-color: ${bpmnElementAccentColor};
        
        --popup-header-entry-selected-color: ${bpmnElementAccentColor};
        --popup-background-color: ${bpmnPaletteBackgroundColor};
        --popup-entry-hover-color: ${bpmnPaletteBackgroundColorHover};
        --popup-border-color: ${colors.transparent};
        --popup-shadow-color: ${bpmnPopupShadowColor};
        --resizer-fill-color: ${bpmnElementAccentColor};
        --resizer-stroke-color: ${bpmnElementAccentColor};

        --search-container-border-color: ${bpmnElementAccentColor};
        --search-container-box-shadow-color: ${bpmnElementAccentColor};
        --search-result-selected-color: ${bpmnSearchResultSelectedColor};
        --snap-line-stroke-color: ${bpmnElementAccentColor};

        *:focus {
            outline: none !important;
        }

        .bpmn-icon-business-rule-task-dmn {
            position: relative;
            &::before {
                content: "";
                display: block;
                margin: 0;
                width: 22px;
                height: 17px;
                background: url("data:image/svg+xml, %3Csvg xmlns='http://www.w3.org/2000/svg' width='22' height='17' viewBox='0 0 30 23' fill='transparent'%3E%3Crect x='1' y='1' width='27.9514' height='21' fill='white'/%3E%3Crect x='1' y='1' width='28' height='6.58824' fill='%23A1A1A1'/%3E%3Cpath d='M1 7.79412V1H29V7.79412M1 7.79412H10.75M1 7.79412V14.8971M29 7.79412H10.75M29 7.79412V14.8971M10.75 7.79412V22M10.75 22H29V14.8971M10.75 22H1V14.8971M1 14.8971H29' stroke='black' stroke-width='1.5'/%3E%3C/svg%3E");
                background-size: cover;
                background-color: white;
            }
        }

        .bpmn-icon-business-rule-task-vdm {
            position: relative;
            &::before {
                content: "";
                display: block;
                margin: 0;
                width: 22px;
                height: 22px;
                background: url("data:image/svg+xml,%3Csvg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M5.5 3.24994C6.32843 3.24994 7 3.92151 7 4.74994H17.5C17.5 3.92151 18.1716 3.24994 19 3.24994C19.8284 3.24994 20.5 3.92151 20.5 4.74994C20.5 5.57837 19.8284 6.24994 19 6.24994C18.1716 6.24994 17.5 5.57837 17.5 4.74994H7C7 5.57837 6.32843 6.24994 5.5 6.24994C4.67157 6.24994 4 5.57837 4 4.74994C4 3.92151 4.67157 3.24994 5.5 3.24994Z' fill='${bpmnPaletteColorUrlEncoded}'/%3E%3Cpath d='M5.5 10.75C6.32843 10.75 7 11.4216 7 12.25H17.4995C17.4997 12.25 17.4998 12.25 17.4999 12.25C17.4999 11.4216 18.1715 10.7501 18.9999 10.75C19.8284 10.75 20.5 11.4217 20.5 12.2501C20.5 13.0786 19.8284 13.7503 18.9999 13.7502C18.1714 13.7502 17.4998 13.0785 17.4999 12.25C17.4998 12.25 17.4997 12.25 17.4995 12.25C9.00016 12.2501 14.4999 19.25 7 19.25C7 20.0785 6.32843 20.75 5.5 20.75C4.67157 20.75 4 20.0785 4 19.25C4 18.4216 4.67157 17.75 5.5 17.75C6.32843 17.75 7 18.4216 7 19.25C14.4999 19.25 9.00016 12.2501 17.4995 12.25H7C7 13.0785 6.32843 13.75 5.5 13.75C4.67157 13.75 4 13.0785 4 12.25C4 11.4216 4.67157 10.75 5.5 10.75Z' fill='${bpmnPaletteColorUrlEncoded}'/%3E%3Cpath d='M7 4.74994H17.5M7 12.25H17.4995M17.4995 12.25C9.00016 12.2501 14.4999 19.25 7 19.25' stroke='${bpmnPaletteColorUrlEncoded}' stroke-width='1.5' fill='none' stroke-linecap='round'/%3E%3C/svg%3E");
                background-size: cover;
            }
        }

        .bpmn-icon-native-ai-task {
            position: relative;

            &::before {
                content: "";
                display: block;
                margin: -2px 0 0 0;
                width: 22px;
                height: 22px;
                background: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='22' height='22' viewBox='0 0 24 24' fill='none'%3E%3Cpath d='M14.125 8.66667V17H11.6875V14.5H8.4375V17H6V8.66667C6 7.75 6.73125 7 7.625 7H12.5C13.3938 7 14.125 7.75 14.125 8.66667ZM11.6875 9.5H8.4375V12H11.6875V9.5Z' fill='${bpmnPaletteColorUrlEncoded}'/%3E%3Cpath d='M19 7H16.5625V17H19V7Z' fill='${bpmnPaletteColorUrlEncoded}'/%3E%3C/svg%3E");
            }
        }
              
        .djs-palette //Moves the palette towards the corner and makes it double column. It seems the palette is not always wrapping when it should, so let's force it to two:
        {
            top: 70px;
            left: 10px;
            width: 123px !important;
            border: 1px solid ${colors.grey_2};
            border-radius: 25px;
            @media ${device.desktop} {
                left: 38px;
                top: 100px;
                width: 110px !important;
            }
            .entry {
                width: 36px;
                @media ${device.desktop} {
                    width: 46px;
                }
            }

            .bpmn-icon-business-rule-task-dmn {
                &::before {
                    margin: 10px auto 0 auto;
                    width: 30px;
                    height: 23px;
                }
                // Hover overlay
                &:hover {
                    &::after{
                        content: "";
                        display: block;
                        margin: 10px auto 0 auto;
                        width: 30px;
                        height: 23px;
                        background-color: ${bpmnElementAccentColor};
                        opacity: 0.5;
                        position: absolute;
                        top: 0;
                        left: 0;
                        right: 0;
                        bottom: 0;
                        z-index: 2;
                    }
                }
            }

            .bpmn-icon-business-rule-task-vdm {
                &::before {
                    margin: 8px auto 0 auto;
                    width: 28px;
                    height: 28px;
                }
                &:hover {
                    &::before {
                        background-image: url("data:image/svg+xml,%3Csvg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M5.5 3.24994C6.32843 3.24994 7 3.92151 7 4.74994H17.5C17.5 3.92151 18.1716 3.24994 19 3.24994C19.8284 3.24994 20.5 3.92151 20.5 4.74994C20.5 5.57837 19.8284 6.24994 19 6.24994C18.1716 6.24994 17.5 5.57837 17.5 4.74994H7C7 5.57837 6.32843 6.24994 5.5 6.24994C4.67157 6.24994 4 5.57837 4 4.74994C4 3.92151 4.67157 3.24994 5.5 3.24994Z' fill='${bpmnElementAccentColorUrlEncoded}'/%3E%3Cpath d='M5.5 10.75C6.32843 10.75 7 11.4216 7 12.25H17.4995C17.4997 12.25 17.4998 12.25 17.4999 12.25C17.4999 11.4216 18.1715 10.7501 18.9999 10.75C19.8284 10.75 20.5 11.4217 20.5 12.2501C20.5 13.0786 19.8284 13.7503 18.9999 13.7502C18.1714 13.7502 17.4998 13.0785 17.4999 12.25C17.4998 12.25 17.4997 12.25 17.4995 12.25C9.00016 12.2501 14.4999 19.25 7 19.25C7 20.0785 6.32843 20.75 5.5 20.75C4.67157 20.75 4 20.0785 4 19.25C4 18.4216 4.67157 17.75 5.5 17.75C6.32843 17.75 7 18.4216 7 19.25C14.4999 19.25 9.00016 12.2501 17.4995 12.25H7C7 13.0785 6.32843 13.75 5.5 13.75C4.67157 13.75 4 13.0785 4 12.25C4 11.4216 4.67157 10.75 5.5 10.75Z' fill='${bpmnElementAccentColorUrlEncoded}'/%3E%3Cpath d='M7 4.74994H17.5M7 12.25H17.4995M17.4995 12.25C9.00016 12.2501 14.4999 19.25 7 19.25' stroke='${bpmnElementAccentColorUrlEncoded}' stroke-width='1.5' fill='none' stroke-linecap='round'/%3E%3C/svg%3E");
                    }
                }
            }

            .bpmn-icon-native-ai-task {
                position: relative;

                &::before {
                    content: "";
                    display: block;
                    margin: 8px auto;
                    width: 28px;
                    height: 28px;
                    background: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='28' height='28' viewBox='0 0 24 24' fill='none'%3E%3Cpath d='M14.125 8.66667V17H11.6875V14.5H8.4375V17H6V8.66667C6 7.75 6.73125 7 7.625 7H12.5C13.3938 7 14.125 7.75 14.125 8.66667ZM11.6875 9.5H8.4375V12H11.6875V9.5Z' fill='${bpmnPaletteColorUrlEncoded}'/%3E%3Cpath d='M19 7H16.5625V17H19V7Z' fill='${bpmnPaletteColorUrlEncoded}'/%3E%3C/svg%3E");
                }

                &.enabled {
                    &:hover {
                        &::before {
                            background: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='28' height='28' viewBox='0 0 24 24' fill='none'%3E%3Cpath d='M14.125 8.66667V17H11.6875V14.5H8.4375V17H6V8.66667C6 7.75 6.73125 7 7.625 7H12.5C13.3938 7 14.125 7.75 14.125 8.66667ZM11.6875 9.5H8.4375V12H11.6875V9.5Z' fill='${bpmnElementAccentColorUrlEncoded}'/%3E%3Cpath d='M19 7H16.5625V17H19V7Z' fill='${bpmnElementAccentColorUrlEncoded}'/%3E%3C/svg%3E");
                        }
                    }
                }

                &.disabled {
                    &::before {
                        background: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='28' height='28' viewBox='0 0 24 24' fill='none'%3E%3Cpath d='M14.125 8.66667V17H11.6875V14.5H8.4375V17H6V8.66667C6 7.75 6.73125 7 7.625 7H12.5C13.3938 7 14.125 7.75 14.125 8.66667ZM11.6875 9.5H8.4375V12H11.6875V9.5Z' fill='${bpmnPaletteDisabledColorUrlEncoded}'/%3E%3Cpath d='M19 7H16.5625V17H19V7Z' fill='${bpmnPaletteDisabledColorUrlEncoded}'/%3E%3C/svg%3E");
                    }
                }
            }
        }
        .djs-palette-entries {
            padding: 5px;
        }
        .djs-palette-toggle {
            visibility: collapse !important; //Hide the collapse button on the toolbox.
        }
        .highlight-obsolete-task:not(.djs-connection) .djs-visual > :nth-child(1) {
            stroke: ${colors.orangeSecondary}  !important; /* color border yellow */
        }
        .djs-overlay {
            pointer-events: none;
        }
        .djs-context-pad {
            background-color: ${bpmnContextPadBackgroundColor};
        }
        .djs-context-pad .entry {
            background: transparent;
            box-shadow: none;
        }
        .djs-popup {
            .entry.selected  {
                .djs-popup-label, [class*="icon"]:before {
                    color: ${colors.white};
                }
                &[data-id='red-color'] {
                background-color: ${colors.warningSecondary};
                }
                &[data-id='orange-color'] {
                    background-color: ${colors.orangeSecondary};
                }
                &[data-id='green-color'] {
                    background-color: ${colors.greenSecondary};
                }
                &[data-id='blue-color'] {
                    background-color: ${colors.blueSecondary};
                }
                &[data-id='purple-color'] {
                    background-color: ${colors.purpleSecondary};
                }
                &[data-id='pink-color'] {
                    background-color: ${colors.pinkSecondary};
                }
            }
        }
        .djs-minimap {
            z-index: 3;
            background-color: ${bpmnElementColor} !important;
        }
        .djs-minimap:not(.open) {
            overflow: hidden;
            display: none;
        }
        .djs-minimap .viewport-dom {
            border: 2px solid ${colors.purplePrimary};
        }
        .highlight-error-scope .djs-visual > :nth-child(1) {
            stroke: ${colors.warningPrimary}  !important; /* color border red */
        }
        .djs-element:not(.djs-connection) .djs-visual {
            .djs-label {
                fill: ${bpmnLabelColor} !important;
            }

            /* BpmnViewer (Process execution) special text color cases */
            /* Success */
            rect[style*="fill: ${hexToRgb(colors.uiGreenLight)};"] + .djs-label,
            circle[style*="fill: ${hexToRgb(colors.uiGreenLight)}"] + .djs-label,
            /* Running */
            rect[style*="fill: ${hexToRgb(colors.uiOrange)};"] + .djs-label,
            circle[style*="fill: ${hexToRgb(colors.uiOrange)}"] + .djs-label {
                fill: ${colors.black} !important;
            }
            /* Error */
            rect[style*="stroke: ${hexToRgb(colors.uiWarning)};"] + .djs-label {
                fill: ${colors.uiWarning} !important;
            }
            rect[style*="stroke: ${hexToRgb(colors.uiWarningDark)};"] + .djs-label {
                fill: ${colors.white} !important;
            }
            rect[style*="stroke: ${hexToRgb(colors.warningTertiary)};"] + .djs-label {
                fill: ${colors.warningTertiary} !important;
            }
            /* Not run */
            rect[style*="stroke: ${hexToRgb(colors.grey_1)};"] + .djs-label,
            circle[style*="stroke: ${hexToRgb(colors.grey_1)}"] + .djs-label {
                fill: ${bpmnNotRunLabelColor} !important;
            }
        }
        .djs-direct-editing-parent {
            background-color: ${bpmnElementColor} !important;
        }
        .djs-search-result {
            background: ${bpmnSearchBackgroundColor};
            &-selected {
                background: ${bpmnSearchBackgroundColorSelected};
            }
        }
        .djs-search-overlay {
            background: ${bpmnSearchResultSelectedColor};
            opacity: 25%;
        }
    }

    .collapse-css-transition {
        transition: height 280ms cubic-bezier(0.4, 0, 0.2, 1);
    }
    .Toastify__toast-container {
        z-index: 99999; // Raise toast above all elements including modals
    }
    .Toastify__progress-bar {
        right: 0;
        transform-origin: right;
    }
    .Toastify__progress-bar--success {
        background-color: ${colors.uiGreen};
    }
    .Toastify__progress-bar--warning {
        background-color: ${colors.orangePrimary};
    }
    .Toastify__toast--error .Toastify__toast-icon {
        align-self: flex-start;
    }
    .monaco-editor {
         .view-overlays .current-line {
            border-color: transparent !important;
         }
        .margin-view-overlays .current-line-margin {
            border-color: transparent !important;
        }
        .editorPlaceholder {
            font-family: "Gilroy-Regular", "regular-fallback" !important;
            color: ${colors.grey_2};
        }
    }
    .react-datepicker-wrapper {
        .react-datepicker__input-container {
            input {
                height: 40px !important;
                border-radius: 20px !important;
                background-color: ${datePickerInputContainerBackgroundColor} !important;
                color: ${datePickerInputContainerColor};
                padding-left: 15px;
            }
        }
    }
    .react-datepicker {
        .react-datepicker__header {
            background-color: ${datePickerHeaderBackgroundColor} !important;
            .react-datepicker__current-month, .react-datepicker__day-name {
                color: ${datePickerHeaderColor} !important;
            }
        }
        background-color: ${datePickerBackgroundColor} !important;
        .react-datepicker__day {
            color: ${datePickerDayColor} !important;
            &:hover {
                background-color: ${datePickerDayHoverBackgroundColor} !important;
            }
        }
        .react-datepicker__day--disabled {
            color: ${datePickerDisabledDayColor} !important;
        }
        .react-datepicker__navigation--previous {
            border-right-color: ${datePickerNavigationColor} !important;
        }
        .react-datepicker__navigation--next {
            border-left-color: ${datePickerNavigationColor} !important;
        }
    }
    a[href='http://bpmn.io'] {
        color: black !important;
    }
    .cc-revoke // Override cookieconsent "Cookie policy"-link style to match the theme
    {
        font-family: "Gilroy-Regular", "regular-fallback";
        font-size: 1rem;
        font-weight: 400;
        font-style: normal;
        line-height: 36px;
        text-decoration: underline;
        color: ${colors.black} !important;
        background-color: ${colors.transparent} !important;
    }
`

